export const BRAND_LANDING_META = {
  title: 'Brand | See what AI says about your brand | QuestionPro',
  description:
    'Audit how ChatGPT, Gemini, and Perplexity recommend your brand. Catch wrong claims, measure share of voice, and fix what AI cites — inside QuestionPro Reputation.',
};

export const BRAND_LANDING_NAV = [
  { href: '#demo', label: 'See it' },
  { href: '#workflow', label: 'How it works' },
  { href: '#faq', label: 'FAQ' },
];

export const BRAND_LANDING_COPY = {
  heroTitle: 'Know what AI says about your brand.',
  heroBody:
    'Brand audits how ChatGPT, Gemini, and Perplexity recommend you when buyers ask. Measure share of voice, catch wrong or stale claims, and turn citations into a fix list — inside QuestionPro Reputation.',
  ctaPrimary: 'Try Brand',
  ctaSecondary: 'See how it works',
  alreadyHint: 'Already use QuestionPro Reputation? Start a Brand project from your workspace.',
  workflowEyebrow: 'HOW IT WORKS',
  workflowTitle: 'Set up once. Audit what AI recommends.',
  workflowBody:
    'Enter your brand and website. Brand generates buyer prompts and a fact sheet, runs the engines, and brings structured answers back for your team.',
  steps: [
    {
      n: '1',
      label: 'BUILD',
      title: 'Start with your brand',
      body: 'Add your brand name, website, and aliases. No separate research stack — Brand lives next to Reviews in Reputation.',
      tip: 'Do I need another tool? No.',
      mock: 'build' as const,
    },
    {
      n: '2',
      label: 'GENERATE',
      title: 'Prompts, competitors, facts',
      body: 'Guided setup drafts competitors, buyer questions, and a fact sheet you can review before the first run.',
      tip: 'Can I edit what AI drafts? Yes.',
      mock: 'generate' as const,
    },
    {
      n: '3',
      label: 'AUDIT',
      title: 'Ask the engines',
      body: 'The same prompts go to ChatGPT, Gemini, and Perplexity so you can compare recommendations in one place.',
      tip: 'One run. Three engines.',
      mock: 'audit' as const,
    },
    {
      n: '4',
      label: 'ACT',
      title: 'Claims, worklist, drift',
      body: 'Verify claims against your fact sheet, prioritize sources to fix, and re-run to prove what improved.',
      tip: 'Insight without a next step? Not here.',
      mock: 'act' as const,
    },
  ],
  demoEyebrow: 'PRODUCT PREVIEW',
  demoTitle: 'Overview, claims, and drift — without guessing.',
  demoBody:
    'Explore a sample Brand audit. Switch between share of voice, claim verification, and what changed after a re-run.',
  audiencesTitle: 'Built for the people behind the brand.',
  audiences: [
    {
      title: 'Brand & product marketing',
      body: 'See whether you make the AI shortlist — and who is winning share of voice.',
    },
    {
      title: 'Content & PR',
      body: 'Find false or stale claims and the web sources engines cite when they answer.',
    },
    {
      title: 'Leadership',
      body: 'Turn an invisible discovery channel into a measurable reputation loop.',
    },
  ],
  faqEyebrow: 'FAQ',
  faqTitle: 'Questions, answered',
  faqBody: 'What teams usually ask before their first Brand audit.',
  faqs: [
    {
      q: 'What is Brand?',
      a: 'Brand is QuestionPro’s AI-answer audit inside Reputation. It shows how ChatGPT, Gemini, and Perplexity recommend your brand on buyer-style questions, then flags inaccurate or stale claims and points you to the sources worth fixing.',
    },
    {
      q: 'How does a Brand audit work?',
      a: 'You enter your brand and website. Brand drafts competitors, buyer prompts, and a fact sheet for review, then asks the same questions across the engines. You get share of voice, recommendation rank, claim checks, and a worklist of next actions.',
    },
    {
      q: 'Which AI engines are covered?',
      a: 'ChatGPT, Gemini, and Perplexity in one run. Answers are collected through APIs so you can compare engines side by side — they may differ from a personalized consumer chat.',
    },
    {
      q: 'Is Brand a replacement for SEO tools?',
      a: 'No. SEO measures keywords and pages. Brand measures whether AI recommends you when buyers ask “what’s the best…?” questions — and whether those answers are accurate.',
    },
    {
      q: 'Do I need to leave QuestionPro Reputation?',
      a: 'No. Brand lives inside Reputation alongside reviews and related reputation workflows, so AI-answer visibility sits next to the rest of your brand program.',
    },
    {
      q: 'What happens when I click Try Brand?',
      a: 'You share your brand and website, then your work email. We create (or find) your QuestionPro organization, then you continue with QuestionPro sign-in. Reputation sets up your Brand audit from the details you entered.',
    },
    {
      q: 'When should I not use Brand (yet)?',
      a: 'If your brand has almost no public web footprint for AI engines to cite, or you only need classic keyword SEO rankings. Brand is built for categories where buyers ask AI for recommendations.',
    },
  ],
  signupTitle: 'Give your next reputation check a Brand audit.',
  signupBody:
    'Enter your brand and website to get started.',
  formBrandLabel: 'Brand name',
  formWebsiteLabel: 'Website',
  formEmailLabel: 'Work email',
  formEmailHint: 'We’ll email you once the audit run is complete.',
  formContinue: 'Continue',
  formStartAudit: 'Start Brand audit',
  formBack: 'Back',
  formSuccessTitle: 'You’re ready.',
  formSuccessBody:
    'Your QuestionPro access is set up. If you are new to QuestionPro, you will receive an email with your login credentials. Continue to sign in, and we’ll create your Brand audit from the brand and website you entered.',
  formSuccessBodyExisting:
    'You already have an account with QuestionPro. Continue to sign in, and we’ll create your Brand audit from the brand and website you entered.',
  formContinueOAuth: 'Sign in to QuestionPro',
  footerNote: 'Brand is part of QuestionPro Reputation.',
};
